<template>
  <div v-if="isLoading">
    <span class="adn-spinner spinner-color-primary"></span>
  </div>
  <figure v-else id="domProductGallery"></figure>
</template>

<script lang="ts">
import {
  computed,
  type ComputedRef,
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
  watch,
  getCurrentInstance,
} from 'vue'
import { useProduct } from '@b2ag/catalog/src/useProduct'
import CloudinaryGalleryPlugin from '@b2ag/plugin-cloudinary-gallery'
import { currentAppBrand } from '@b2ag/app-brands'
import { i18n } from '@b2ag/locale'
import type { Product } from '../../../domain/product'
import { getCloudinaryProductDefaultImage } from '../../../services/products-images.constants'
import { hasImage } from '../../../services/product.business'
import { getConfig } from '@b2ag/micro-front-tools'

// extract product id from image url since target format is /products/productId
// URLs hardcoded here because it is simpler and temporary
// once image sources are all migrated to cloudinary we do not need to replace in image URLs
const AXE_ENVIRONNEMENT_IMAGE_URL_CLOUDFRONT = '^https:\\/\\/[a-z0-9]+\\.cloudfront\\.net\\/public\\/products\\/'
const IMAGE_URL_ORIGIN_REGEXP_ALTERNATIVES: string[] = [
  '^https:\\/\\/idfmarketplace[a-z]*\\.blob\\.core\\.windows\\.net\\/img-products\\/',
  '^https:\\/\\/res\\.cloudinary\\.com\\/[a-z]+\\/image\\/upload\\/(?:v[0-9]+\\/)?products\\/',
  AXE_ENVIRONNEMENT_IMAGE_URL_CLOUDFRONT,
]
const IMAGE_URL_EPISENS = '^https:\\/\\/res\\.cloudinary\\.com\\/[a-z]+\\/image\\/upload\\/(?:v[0-9]+\\/)?EPISENS\\/'

const IMAGE_URL_ORIGIN_REGEXP = new RegExp(IMAGE_URL_ORIGIN_REGEXP_ALTERNATIVES.join('|'))
const AXE_ENVIRONNEMENT_CLOUDFRONT_REGEX = new RegExp(AXE_ENVIRONNEMENT_IMAGE_URL_CLOUDFRONT)
const IMAGE_URL_EPISENS_REGEX = new RegExp(IMAGE_URL_EPISENS)

export default defineComponent({
  props: {
    productId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const isLoading = ref<boolean>(false)
    const { product } = useProduct(props.productId) as Record<'product', ComputedRef<Product>>
    let cloudinaryWidget: any | null

    const customImageUrl = ref('')

    const defaultImage = computed(() => {
      if (product.value?.kind) return getCloudinaryProductDefaultImage(product.value.kind, product.value.type)
      return null
    })
    const productImage = computed(() => {
      const blobUrl = product.value.images[0]
      if (blobUrl && AXE_ENVIRONNEMENT_CLOUDFRONT_REGEX.test(blobUrl)) {
        return blobUrl.replace(AXE_ENVIRONNEMENT_CLOUDFRONT_REGEX, 'products/FOURNISSEURS/auto_axe_environnement/')
      }
      if (blobUrl && IMAGE_URL_EPISENS_REGEX.test(blobUrl)) {
        return blobUrl.replace(IMAGE_URL_EPISENS_REGEX, 'EPISENS/')
      }
      return blobUrl && blobUrl.replace(IMAGE_URL_ORIGIN_REGEXP, 'products/')
    })

    const hasImageComputed = computed(() => {
      return hasImage(product.value)
    })

    const publicId = computed(() => {
      if (customImageUrl.value) return customImageUrl.value.replace(IMAGE_URL_ORIGIN_REGEXP, 'products/')
      return hasImageComputed.value ? productImage.value : defaultImage.value
    })

    const productsMediaAsset = computed(() => {
      const mainImageAsset = {
        publicId: publicId.value,
        altText: product?.value?.name ?? i18n._('Image principale'),
        mediaType: 'image',
      }
      const otherImagesAsset = {
        tag: `gallery_product_${props.productId}`,
        altText: product?.value?.name ?? '',
        mediaType: 'image',
      }
      const VIDEO_DURATION_100PERCENT = {
        duration: '100p',
      }
      const videoAsset = {
        tag: `gallery_product_${props.productId}`,
        mediaType: 'video',
        transformation: {
          transformation: [VIDEO_DURATION_100PERCENT],
        },
      }
      const model3DAsset = {
        tag: `gallery_product_${props.productId}`,
        mediaType: '3d',
      }

      if (product.value?.organic) {
        const CLOUDINARY_LOG0_AB_TRANSFORMATION = {
          aladin: 'organic_logo_overlay',
          farmi: 'farmi_organic_logo_overlay',
        }

        const getTransformations = (context: string) => {
          return {
            transformation: {
              transformation: [CLOUDINARY_LOG0_AB_TRANSFORMATION[context], VIDEO_DURATION_100PERCENT],
            },
            thumbnailTransformation: {
              // needed to override media transformation for thumbnail
              transformation: [],
            },
          }
        }
        const contextualProductMediaAsset = (transformation) => {
          return [
            {
              ...mainImageAsset,
              ...transformation,
            },
            {
              ...otherImagesAsset,
              ...transformation,
            },
            {
              ...videoAsset,
              ...transformation,
            },
            {
              ...model3DAsset,
              ...transformation,
            },
          ]
        }
        return contextualProductMediaAsset(getTransformations(currentAppBrand))
      }
      return [mainImageAsset, otherImagesAsset, videoAsset, model3DAsset]
    })

    const isCloudinaryGalleryScriptLoaded = ref<boolean>(false)

    async function loadCloudinaryGalleryScript() {
      await new Promise((resolve) => {
        if (window.cloudinary?.galleryWidget) resolve('')
        getCurrentInstance()?.appContext.app.use(CloudinaryGalleryPlugin, { onLoadCallback: resolve })
      })
      isCloudinaryGalleryScriptLoaded.value = true
    }

    function renderCloudinaryGallery() {
      if (!publicId.value) return
      cloudinaryWidget = window.cloudinary?.galleryWidget({
        container: '#domProductGallery',
        cloudName: getConfig('CLOUDINARY_BUCKET_NAME'),
        mediaAssets: productsMediaAsset.value,
        accessibilityProps: {
          mediaAltSource: 'auto',
        },
        carouselLocation: 'bottom',
        themeProps: {
          active: '#085691',
          onPrimary: '#183c67',
          primary: '#ffffff',
        },
        navigationButtonProps: {
          iconColor: '#085691',
          color: '#FFFFFF',
          shape: 'round',
          size: 40,
        },
        carouselOffset: 8,
        thumbnailProps: {
          height: 64,
          navigationColor: '#ffffff',
          navigationIconColor: '#085691',
          selectedBorderPosition: 'all',
          navigationShape: 'none',
          selectedBorderWidth: 1,
        },
        zoomProps: {
          level: 3,
          tipText: i18n._('Cliquer pour agrandir'),
          tipTouchText: i18n._('Toucher pour agrandir'),
        },
        videoProps: {
          controls: 'play',
          sound: true,
          autoplay: false,
        },
        loaderProps: {
          style: 'circle',
        },
      })
      cloudinaryWidget?.render()
    }

    onMounted(async () => {
      isLoading.value = true
      await loadCloudinaryGalleryScript()
      isLoading.value = false
    })

    onUnmounted(() => {
      cloudinaryWidget?.destroy()
    })

    const isProductAndGalleryScriptLoaded = computed(() => {
      return isCloudinaryGalleryScriptLoaded.value && publicId.value
    })

    watch(isProductAndGalleryScriptLoaded, () => {
      if (!isProductAndGalleryScriptLoaded.value) return
      renderCloudinaryGallery()
    })

    return {
      isLoading,
    }
  },
})
</script>

<style lang="scss" scoped>
#domProductGallery {
  grid-column-end: span 2;
  min-width: 1px; // hack for cloudinary gallery widget
  height: auto;
  width: 80%;

  @media (min-width: $breakpoint-md) {
    grid-column-end: span 1;
  }
}
</style>
